/**
 * Charts for the Building Electrification Policy panel.
 * Uses existing data from buildingsBauPathway and buildingEmissionsTimeSeries (from policyTimeSeriesService / calculatePolicyImpact).
 */
import React, { useMemo } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { BuildingsBauPathway, BuildingsBauScenario } from '../../services/buildingsBauPathways';
import {
  getPathwayRngShare,
  getPathwayTherms,
  getPathwayKwh,
  NG_EMISSION_FACTOR_KG_PER_THERM,
} from '../../services/buildingsBauPathways';
import type { BuildingEmissionsTimeSeries } from './PolicyImpactTool';
import type { BuildingEnergyComparisonSeries } from './buildingElectrificationChartTypes';
import { legendLabelsReflectLineDash } from './chartJsLegendLineDash';

export type { BuildingEnergyComparisonSeries } from './buildingElectrificationChartTypes';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

const COLORS = {
  electrified: 'rgb(34, 197, 94)',   // green
  electrifiedPolicy: 'rgb(21, 128, 61)', // darker green (policy)
  fossil: 'rgb(234, 88, 12)',        // orange
  fossilPolicy: 'rgb(180, 83, 9)',   // darker orange (policy)
  electricity: 'rgb(59, 130, 246)', // blue
  electricityBau: 'rgb(125, 211, 252)', // lighter blue dashed BAU
  naturalGas: 'rgb(234, 88, 12)',   // orange
  naturalGasBau: 'rgb(180, 180, 180)', // gray dashed BAU therms
  bau: 'rgb(107, 114, 128)',         // gray
  policy: 'rgb(34, 197, 94)',        // green
  rng: 'rgb(168, 85, 247)',         // purple
};


const mtFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1, notation: 'standard' });
const pctFormatter = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 });
const numFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0, notation: 'standard' });

const CHART_YEAR_MIN = 2023;
const CHART_YEAR_MAX = 2045;

const commonOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        usePointStyle: false,
        boxWidth: 28,
        generateLabels: legendLabelsReflectLineDash,
      },
    },
    tooltip: {
      mode: 'index',
      intersect: false,
    },
  },
};

// ---------------------------------------------------------------------------
// 1. Stock Evolution Chart
// ---------------------------------------------------------------------------
export interface BuildingsStockChartProps {
  pathway: BuildingsBauPathway | null;
  /**
   * Local building electrification target at 2045 (0–100), same as policy heat pump slider.
   * Policy line = BAU electrifiable-pool electrified % plus this fraction of the **remaining electrifiable**
   * pool, ramped linearly 2023→2045 (matches policy time series).
   */
  localHeatPumpAdoptionPercent2045?: number;
}

export const BuildingsStockChart: React.FC<BuildingsStockChartProps> = ({
  pathway,
  localHeatPumpAdoptionPercent2045 = 0,
}) => {
  const {
    labels,
    bauElectrifiedPct,
    bauFossilPct,
    policyElectrifiedPct,
    policyFossilPct,
  } = useMemo(() => {
    if (!pathway?.years?.length || !pathway.stockElectrifiedByYear?.length || !pathway.stockFossilByYear?.length) {
      return {
        labels: [] as string[],
        bauElectrifiedPct: [] as number[],
        bauFossilPct: [] as number[],
        policyElectrifiedPct: [] as number[],
        policyFossilPct: [] as number[],
      };
    }
    const years = pathway.years.filter((y) => y >= CHART_YEAR_MIN && y <= CHART_YEAR_MAX);
    const targetHp = Math.max(0, Math.min(100, localHeatPumpAdoptionPercent2045));
    const totalSteps = CHART_YEAR_MAX - CHART_YEAR_MIN;

    const bauElectrifiedPct = years.map((y) => {
      const i = pathway.years.indexOf(y);
      return (pathway.stockElectrifiedByYear![i] ?? 0) * 100;
    });
    const bauFossilPct = years.map((y) => {
      const i = pathway.years.indexOf(y);
      return (pathway.stockFossilByYear![i] ?? 0) * 100;
    });

    const policyElectrifiedPct = years.map((y, idx) => {
      const yearsElapsed = y - CHART_YEAR_MIN;
      const ramp = y <= CHART_YEAR_MIN ? 0 : Math.min(1, yearsElapsed / totalSteps);
      const elecFrac = (targetHp / 100) * ramp;
      const bE = bauElectrifiedPct[idx] ?? 0;
      const bF = bauFossilPct[idx] ?? 0;
      return Math.min(100, bE + bF * elecFrac);
    });
    const policyFossilPct = policyElectrifiedPct.map((pe) => Math.max(0, 100 - pe));

    return {
      labels: years.map(String),
      bauElectrifiedPct,
      bauFossilPct,
      policyElectrifiedPct,
      policyFossilPct,
    };
  }, [pathway, localHeatPumpAdoptionPercent2045]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Electrified share (BAU, electrifiable pool)',
          data: bauElectrifiedPct,
          borderColor: COLORS.electrified,
          backgroundColor: 'rgba(34, 197, 94, 0.08)',
          borderDash: [6, 4],
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
        {
          label: 'Electrified share (Policy)',
          data: policyElectrifiedPct,
          borderColor: COLORS.electrifiedPolicy,
          backgroundColor: 'rgba(21, 128, 61, 0.12)',
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
        {
          label: 'Unelectrified electrifiable (BAU)',
          data: bauFossilPct,
          borderColor: COLORS.fossil,
          backgroundColor: 'rgba(234, 88, 12, 0.08)',
          borderDash: [6, 4],
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
        {
          label: 'Unelectrified electrifiable (Policy)',
          data: policyFossilPct,
          borderColor: COLORS.fossilPolicy,
          backgroundColor: 'rgba(180, 83, 9, 0.1)',
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
      ],
    }),
    [labels, bauElectrifiedPct, bauFossilPct, policyElectrifiedPct, policyFossilPct]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          min: 0,
          max: 100,
          title: { display: true, text: 'Share of electrifiable gas (%)' },
          ticks: { callback: (v) => (typeof v === 'number' ? `${v}%` : v) },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        tooltip: {
          ...commonOptions.plugins?.tooltip,
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y != null ? pctFormatter.format(Number(ctx.parsed.y) / 100) : '—'}`,
          },
        },
      },
    }),
    []
  );

  if (!pathway || labels.length === 0) {
    return (
      <div className="building-electrification-chart-card">
        <h5>Building Electrification Over Time (BAU vs Policy)</h5>
        <p className="building-electrification-chart-empty">Pathway data not available for this location.</p>
      </div>
    );
  }

  return (
    <div className="building-electrification-chart-card">
      <h5>Building Electrification Over Time (BAU vs Policy)</h5>
      <p className="building-electrification-chart-note">
        BAU from selected buildings pathway. Policy applies the local heat pump target (ramped 2023–2045) to the remaining fossil-heated
        share for illustration; operating therms/kWh use the energy chart below.
      </p>
      <div className="building-electrification-chart-container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 2. Energy Transition Chart (dual-axis: therms + kWh)
// ---------------------------------------------------------------------------
export interface BuildingsEnergyChartProps {
  pathway: BuildingsBauPathway | null;
  /** When set, shows BAU vs policy therms/kWh (same pipeline as bills/costs). */
  energyComparison?: BuildingEnergyComparisonSeries | null;
}

export const BuildingsEnergyChart: React.FC<BuildingsEnergyChartProps> = ({ pathway, energyComparison }) => {
  const fallbackFromPathway = useMemo(() => {
    if (!pathway?.years?.length || !pathway.thermsByYear?.length || !pathway.kwhByYear?.length) {
      return null;
    }
    const years = pathway.years.filter((y) => y >= CHART_YEAR_MIN && y <= CHART_YEAR_MAX);
    const therms = years.map((y) => pathway.thermsByYear[pathway.years.indexOf(y)] ?? 0);
    const kwh = years.map((y) => pathway.kwhByYear[pathway.years.indexOf(y)] ?? 0);
    return { labels: years.map(String), therms, kwh };
  }, [pathway]);

  const labels = energyComparison?.labels?.length ? energyComparison.labels : fallbackFromPathway?.labels ?? [];
  const comparisonOk =
    !!energyComparison &&
    energyComparison.labels.length > 0 &&
    energyComparison.bauTherms.length === energyComparison.labels.length &&
    energyComparison.policyTherms.length === energyComparison.labels.length &&
    energyComparison.bauKwh.length === energyComparison.labels.length &&
    energyComparison.policyKwh.length === energyComparison.labels.length;

  const data = useMemo(() => {
    if (energyComparison && comparisonOk) {
      return {
        labels: energyComparison.labels,
        datasets: [
          {
            label: 'Natural gas — BAU (therms)',
            data: energyComparison.bauTherms,
            borderColor: COLORS.naturalGasBau,
            backgroundColor: 'rgba(180, 180, 180, 0.12)',
            borderDash: [6, 4],
            tension: 0.2,
            fill: false,
            pointRadius: 2,
            yAxisID: 'yTherms',
          },
          {
            label: 'Natural gas — Policy (therms)',
            data: energyComparison.policyTherms,
            borderColor: COLORS.naturalGas,
            backgroundColor: 'rgba(234, 88, 12, 0.12)',
            tension: 0.2,
            fill: false,
            pointRadius: 2,
            yAxisID: 'yTherms',
          },
          {
            label: 'Electricity — BAU (kWh)',
            data: energyComparison.bauKwh,
            borderColor: COLORS.electricityBau,
            backgroundColor: 'rgba(125, 211, 252, 0.12)',
            borderDash: [6, 4],
            tension: 0.2,
            fill: false,
            pointRadius: 2,
            yAxisID: 'yKwh',
          },
          {
            label: 'Electricity — Policy (kWh)',
            data: energyComparison.policyKwh,
            borderColor: COLORS.electricity,
            backgroundColor: 'rgba(59, 130, 246, 0.12)',
            tension: 0.2,
            fill: false,
            pointRadius: 2,
            yAxisID: 'yKwh',
          },
        ],
      };
    }
    if (fallbackFromPathway) {
      return {
        labels: fallbackFromPathway.labels,
        datasets: [
          {
            label: 'Natural Gas (therms) — BAU pathway only',
            data: fallbackFromPathway.therms,
            borderColor: COLORS.naturalGas,
            backgroundColor: 'rgba(234, 88, 12, 0.15)',
            tension: 0.2,
            fill: false,
            pointRadius: 2,
            yAxisID: 'yTherms',
          },
          {
            label: 'Electricity (kWh) — BAU pathway only',
            data: fallbackFromPathway.kwh,
            borderColor: COLORS.electricity,
            backgroundColor: 'rgba(59, 130, 246, 0.15)',
            tension: 0.2,
            fill: false,
            pointRadius: 2,
            yAxisID: 'yKwh',
          },
        ],
      };
    }
    return { labels: [] as string[], datasets: [] };
  }, [comparisonOk, energyComparison, fallbackFromPathway]);

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        x: { title: { display: true, text: 'Year' } },
        yTherms: {
          type: 'linear',
          position: 'left',
          title: { display: true, text: 'Natural Gas (therms)' },
          ticks: { callback: (v) => (typeof v === 'number' ? numFormatter.format(v) : v) },
        },
        yKwh: {
          type: 'linear',
          position: 'right',
          title: { display: true, text: 'Electricity (kWh)' },
          grid: { drawOnChartArea: false },
          ticks: { callback: (v) => (typeof v === 'number' ? numFormatter.format(v) : v) },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        tooltip: {
          ...commonOptions.plugins?.tooltip,
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              const label = ctx.dataset.label || '';
              if (label.includes('therms')) return `${label}: ${typeof v === 'number' ? numFormatter.format(v) : '—'}`;
              return `${label}: ${typeof v === 'number' ? numFormatter.format(v) : '—'}`;
            },
          },
        },
      },
    }),
    []
  );

  if (labels.length === 0) {
    return (
      <div className="building-electrification-chart-card">
        <h5>Building Energy: BAU vs Policy (Therms &amp; Electricity)</h5>
        <p className="building-electrification-chart-empty">
          {energyComparison && !comparisonOk
            ? 'Energy comparison data is incomplete.'
            : 'Load the policy chart above first, or pathway data is not available for this location.'}
        </p>
      </div>
    );
  }

  return (
    <div className="building-electrification-chart-card">
      <h5>Building Energy: BAU vs Policy (Therms &amp; Electricity)</h5>
      {!comparisonOk && (
        <p className="building-electrification-chart-note">
          Showing BAU pathway only until scenario data is available (open the main policy view to warm the baseline cache).
        </p>
      )}
      {comparisonOk && (
        <p className="building-electrification-chart-note">
          Same building-energy pipeline as operating costs: BAU pathway, then efficiency + heat pump + demand response. Dashed = BAU, solid
          = policy.
        </p>
      )}
      <div className="building-electrification-chart-container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 3. Emissions Comparison: BAU vs Policy
// ---------------------------------------------------------------------------
export interface BuildingsEmissionsChartProps {
  buildingEmissionsTimeSeries: BuildingEmissionsTimeSeries | null;
}

export const BuildingsEmissionsChart: React.FC<BuildingsEmissionsChartProps> = ({ buildingEmissionsTimeSeries }) => {
  const { labels, bauTotal, policyTotal } = useMemo(() => {
    if (!buildingEmissionsTimeSeries?.baseline?.length || !buildingEmissionsTimeSeries?.policy?.length) {
      return { labels: [] as string[], bauTotal: [] as number[], policyTotal: [] as number[] };
    }
    const b = buildingEmissionsTimeSeries.baseline;
    const p = buildingEmissionsTimeSeries.policy;
    const labels = b.map((d) => String(d.year));
    const bauTotal = b.map((d) => d.naturalGas + d.electricity);
    const policyTotal = p.map((d) => d.naturalGas + d.electricity);
    return { labels, bauTotal, policyTotal };
  }, [buildingEmissionsTimeSeries]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'BAU (buildings)',
          data: bauTotal,
          borderColor: COLORS.bau,
          backgroundColor: 'rgba(107, 114, 128, 0.15)',
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
        {
          label: 'Policy (buildings)',
          data: policyTotal,
          borderColor: COLORS.policy,
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
      ],
    }),
    [labels, bauTotal, policyTotal]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          min: 0,
          title: { display: true, text: 'MT CO₂e' },
          ticks: { callback: (v) => (typeof v === 'number' ? mtFormatter.format(v) : v) },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        tooltip: {
          ...commonOptions.plugins?.tooltip,
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y != null ? mtFormatter.format(Number(ctx.parsed.y)) : '—'} MT CO₂e`,
          },
        },
      },
    }),
    []
  );

  if (!buildingEmissionsTimeSeries || labels.length === 0) {
    return (
      <div className="building-electrification-chart-card">
        <h5>Building Emissions: BAU vs Policy</h5>
        <p className="building-electrification-chart-empty">Emissions time series not loaded yet. Change a lever or wait for the chart above to load.</p>
      </div>
    );
  }

  return (
    <div className="building-electrification-chart-card">
      <h5>Building Emissions: BAU vs Policy</h5>
      <div className="building-electrification-chart-container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 4. Emissions Stack: NG + Electricity (BAU)
// ---------------------------------------------------------------------------
export const BuildingsEmissionsStackChart: React.FC<BuildingsEmissionsChartProps> = ({ buildingEmissionsTimeSeries }) => {
  const { labels, naturalGas, electricity } = useMemo(() => {
    if (!buildingEmissionsTimeSeries?.baseline?.length) {
      return { labels: [] as string[], naturalGas: [] as number[], electricity: [] as number[] };
    }
    const b = buildingEmissionsTimeSeries.baseline;
    const labels = b.map((d) => String(d.year));
    const naturalGas = b.map((d) => d.naturalGas);
    const electricity = b.map((d) => d.electricity);
    return { labels, naturalGas, electricity };
  }, [buildingEmissionsTimeSeries]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Natural Gas',
          data: naturalGas,
          borderColor: COLORS.naturalGas,
          backgroundColor: 'rgba(234, 88, 12, 0.6)',
          tension: 0.2,
          fill: true,
          stack: 'stack1',
          pointRadius: 1,
        },
        {
          label: 'Electricity',
          data: electricity,
          borderColor: COLORS.electricity,
          backgroundColor: 'rgba(59, 130, 246, 0.6)',
          tension: 0.2,
          fill: true,
          stack: 'stack1',
          pointRadius: 1,
        },
      ],
    }),
    [labels, naturalGas, electricity]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          stacked: true,
          min: 0,
          title: { display: true, text: 'MT CO₂e' },
          ticks: { callback: (v) => (typeof v === 'number' ? mtFormatter.format(v) : v) },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        tooltip: {
          ...commonOptions.plugins?.tooltip,
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y != null ? mtFormatter.format(Number(ctx.parsed.y)) : '—'} MT CO₂e`,
          },
        },
      },
    }),
    []
  );

  if (!buildingEmissionsTimeSeries || labels.length === 0) {
    return (
      <div className="building-electrification-chart-card">
        <h5>Building Emissions by Source (BAU)</h5>
        <p className="building-electrification-chart-empty">Emissions time series not loaded yet.</p>
      </div>
    );
  }

  return (
    <div className="building-electrification-chart-card">
      <h5>Building Emissions by Source (BAU)</h5>
      <div className="building-electrification-chart-container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 5. RNG Impact: RNG share over time (ramp when override set)
// ---------------------------------------------------------------------------
export interface RngImpactChartProps {
  pathway: BuildingsBauPathway | null;
  /** When set, show ramp from pathway 2023 to this 2045 share (%). */
  rngOverridePercent?: number;
}

export const RngImpactChart: React.FC<RngImpactChartProps> = ({ pathway, rngOverridePercent }) => {
  const { labels, rngPct } = useMemo(() => {
    if (!pathway?.years?.length || !pathway.rngShareByYear?.length) {
      return { labels: [] as string[], rngPct: [] as number[] };
    }
    const years = pathway.years.filter((y) => y >= CHART_YEAR_MIN && y <= CHART_YEAR_MAX);
    const baseYear = 2023;
    const targetYear = 2045;
    const denom = targetYear - baseYear;
    const baseShare = getPathwayRngShare(pathway, baseYear);
    const targetShare = rngOverridePercent != null ? rngOverridePercent / 100 : getPathwayRngShare(pathway, targetYear);

    const rngPct = years.map((y) => {
      if (rngOverridePercent != null) {
        const t = y <= baseYear ? 0 : y >= targetYear ? 1 : (y - baseYear) / denom;
        return (baseShare + (targetShare - baseShare) * t) * 100;
      }
      return getPathwayRngShare(pathway, y) * 100;
    });
    return { labels: years.map(String), rngPct };
  }, [pathway, rngOverridePercent]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'RNG Share (%)',
          data: rngPct,
          borderColor: COLORS.rng,
          backgroundColor: 'rgba(168, 85, 247, 0.2)',
          tension: 0.2,
          fill: true,
          pointRadius: 2,
        },
      ],
    }),
    [labels, rngPct]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          min: 0,
          max: 100,
          title: { display: true, text: 'RNG Share (%)' },
          ticks: { callback: (v) => (typeof v === 'number' ? `${v}%` : v) },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        tooltip: {
          ...commonOptions.plugins?.tooltip,
          callbacks: {
            label: (ctx) => `RNG: ${ctx.parsed.y != null ? Number(ctx.parsed.y).toFixed(1) : '—'}%`,
          },
        },
      },
    }),
    []
  );

  if (!pathway || labels.length === 0) {
    return (
      <div className="building-electrification-chart-card">
        <h5>Impact of Renewable Natural Gas</h5>
        <p className="building-electrification-chart-empty">Pathway data not available.</p>
      </div>
    );
  }

  return (
    <div className="building-electrification-chart-card">
      <h5>Impact of Renewable Natural Gas</h5>
      <p className="building-electrification-chart-note">
        {rngOverridePercent != null
          ? `Ramp to ${rngOverridePercent}% by 2045 (panel override).`
          : 'RNG share from BAU pathway.'}
      </p>
      <div className="building-electrification-chart-container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 6. BAU Scenario Comparison: Conservative vs Current Policy vs Accelerated
// ---------------------------------------------------------------------------
export interface BuildingsBauComparisonChartProps {
  /** Pathways for all three BAU scenarios (from getBuildingsBauPathway). */
  pathwaysByScenario: Record<BuildingsBauScenario, BuildingsBauPathway> | null;
  /** 2023 electricity carbon intensity (g CO₂e/kWh) for grid ramp. */
  baseGPerKwh: number;
  /** Year grid reaches zero emissions; used for g/kWh ramp. */
  cleanElectricityYear: number;
}

/** Compute BAU-only building emissions (MT CO₂e) for one pathway and one year. Reuses same formula as policyTimeSeriesService. */
function bauBuildingEmissionsForYear(
  pathway: BuildingsBauPathway,
  year: number,
  baseGPerKwh: number,
  cleanElectricityYear: number
): number {
  const therms = getPathwayTherms(pathway, year);
  const kwh = getPathwayKwh(pathway, year);
  const rngShare = getPathwayRngShare(pathway, year);
  const denom = Math.max(1, cleanElectricityYear - 2023);
  const t = Math.max(0, Math.min(1, (year - 2023) / denom));
  const gPerKwh = baseGPerKwh * (1 - t);
  const ngMT = (therms * (1 - rngShare) * NG_EMISSION_FACTOR_KG_PER_THERM) / 1000;
  const elecMT = (kwh * gPerKwh) / 1_000_000;
  return ngMT + elecMT;
}

export const BuildingsBauComparisonChart: React.FC<BuildingsBauComparisonChartProps> = ({
  pathwaysByScenario,
  baseGPerKwh,
  cleanElectricityYear,
}) => {
  const { labels, conservativeMT, currentPolicyMT, acceleratedMT } = useMemo(() => {
    if (!pathwaysByScenario) {
      return { labels: [] as string[], conservativeMT: [] as number[], currentPolicyMT: [] as number[], acceleratedMT: [] as number[] };
    }
    const years: number[] = [];
    for (let y = CHART_YEAR_MIN; y <= CHART_YEAR_MAX; y++) years.push(y);
    const conservativeMT = years.map((y) =>
      bauBuildingEmissionsForYear(pathwaysByScenario.conservative, y, baseGPerKwh, cleanElectricityYear)
    );
    const currentPolicyMT = years.map((y) =>
      bauBuildingEmissionsForYear(pathwaysByScenario.current_policy, y, baseGPerKwh, cleanElectricityYear)
    );
    const acceleratedMT = years.map((y) =>
      bauBuildingEmissionsForYear(pathwaysByScenario.accelerated, y, baseGPerKwh, cleanElectricityYear)
    );
    return {
      labels: years.map(String),
      conservativeMT,
      currentPolicyMT,
      acceleratedMT,
    };
  }, [pathwaysByScenario, baseGPerKwh, cleanElectricityYear]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Conservative BAU',
          data: conservativeMT,
          borderColor: 'rgb(148, 163, 184)',
          backgroundColor: 'rgba(148, 163, 184, 0.1)',
          borderDash: [8, 4],
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
        {
          label: 'Current Policy BAU',
          data: currentPolicyMT,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
        {
          label: 'Accelerated BAU',
          data: acceleratedMT,
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          borderWidth: 3,
          tension: 0.2,
          fill: false,
          pointRadius: 3,
        },
      ],
    }),
    [labels, conservativeMT, currentPolicyMT, acceleratedMT]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          min: 0,
          title: { display: true, text: 'MT CO₂e (buildings only)' },
          ticks: { callback: (v) => (typeof v === 'number' ? mtFormatter.format(v) : v) },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        tooltip: {
          ...commonOptions.plugins?.tooltip,
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y != null ? mtFormatter.format(Number(ctx.parsed.y)) : '—'} MT CO₂e`,
          },
        },
      },
    }),
    []
  );

  if (!pathwaysByScenario || labels.length === 0) {
    return (
      <div className="building-electrification-chart-card">
        <h5>Buildings BAU Scenario Comparison (Emissions)</h5>
        <p className="building-electrification-chart-empty">Pathway data not available for this location.</p>
      </div>
    );
  }

  return (
    <div className="building-electrification-chart-card">
      <h5>Buildings BAU Scenario Comparison (Emissions)</h5>
      <p className="building-electrification-chart-note">
        BAU-only building emissions (no local electrification). Grid decarbonizes to zero by {cleanElectricityYear}.
      </p>
      <div className="building-electrification-chart-container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};
